import React, { useState, useEffect } from 'react';
import axios from 'axios';
import backendUrl from '../../BackendUrl';
import UploadImage from '../../components/UploadImage';
import '../../styles/SetPart.css';

const SetPart = () => {
  // State for parts list
  const [parts, setParts] = useState([]);
  // State for modal visibility
  const [isCanvasVisible, setIsCanvasVisible] = useState(false);
  // State to distinguish between add and edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  // The part selected for editing (or null when adding)
  const [selectedPart, setSelectedPart] = useState(null);
  // Retrieve admin token from local storage
  const adminToken = localStorage.getItem('adminToken');

  // Form data for part details
  const [formData, setFormData] = useState({
    part: '',
    description: '',
    dependencies: '',
    vehicles: '',
    price: '',
    ready: false
  });

  // Fetch parts when the component mounts
  useEffect(() => {
    fetchParts();
  }, []);

  const fetchParts = async () => {
    try {
      const res = await axios.get(`${backendUrl}/api/parts/get-part`);
      setParts(res.data);
    } catch (error) {
      console.error('Error fetching parts:', error);
    }
  };

  // Opens the modal for adding a new part
  const handleAddClick = () => {
    setIsEditMode(false);
    setSelectedPart(null);
    setFormData({ part: '', description: '', dependencies: '', vehicles: '', price: '' });
    setIsCanvasVisible(true);
  };

  // Opens the modal for editing an existing part and pre-fills the form
  const handleEditClick = (part) => {
    setIsEditMode(true);
    setSelectedPart(part);
    setFormData({
      part: part.part,
      description: part.description,
      dependencies: part.dependencies ? part.dependencies.join(', ') : '',
      vehicles: part.vehicles ? part.vehicles.join(', ') : '',
      price: part.price || ''
      ,
      ready: !!part.ready
    });
    setIsCanvasVisible(true);
  };

  // Closes the modal canvas
  const handleCancel = () => {
    setIsCanvasVisible(false);
  };

  // Handle changes in the form inputs
  const handleFormChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // Handle form submission for both add and edit
  const handleFormSubmit = async (e) => {
    e.preventDefault();

    try {
      if (isEditMode && selectedPart) {
        // Update an existing part
        const updatedData = {
          ...formData,
          dependencies: formData.dependencies.split(',').map(dep => dep.trim()).filter(Boolean),
          vehicles: formData.vehicles.split(',').map(veh => veh.trim()).filter(Boolean),
          price: Number(formData.price)
        };

        await axios.put(
          `${backendUrl}/api/parts/update-part/${selectedPart._id}`,
          updatedData,
          { headers: { Authorization: `Bearer ${adminToken}` } }
        );
      } else {
        // Create a new part (only part and description are required)
        const newPartData = { part: formData.part, description: formData.description, ready: !!formData.ready };

        await axios.post(
          `${backendUrl}/api/parts/add-part`,
          newPartData,
          { headers: { Authorization: `Bearer ${adminToken}` } }
        );
      }
      fetchParts();
      setIsCanvasVisible(false);
    } catch (error) {
      console.error('Error saving part:', error);
    }
  };

  // Delete a part
  const handleDelete = async (partId) => {
    if (window.confirm('Are you sure you want to delete this part?')) {
      try {
        await axios.delete(`${backendUrl}/api/parts/delete-part/${partId}`, {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        fetchParts();
      } catch (error) {
        console.error('Error deleting part:', error);
      }
    }
  };

  // Refresh parts on successful image upload
  const handleUploadSuccess = () => {
    fetchParts();
  };

  return (
    <div className="admin-parts-page">
      <h1>Manage Parts</h1>
      <button onClick={handleAddClick} className="add-part-btn">Add Part</button>

      <div className="parts-list">
        {parts.map((part) => (
          <div key={part._id} className="part-card">
            <p><strong>{part.part}</strong>: {part.description}</p>
            <img src={part.partImg} alt={part.part} className="part-img" />
            <div className="part-actions">
              <button onClick={() => handleEditClick(part)}>Edit</button>
              <button onClick={() => handleDelete(part._id)}>Delete</button>
            </div>
            <UploadImage 
              uploadUrl="/api/parts/upload-part"
              id={part._id}
              onUploadSuccess={handleUploadSuccess}
            />
          </div>
        ))}
      </div>

      {/* Modal canvas for add/edit form */}
      {isCanvasVisible && (
        <div className="part-canvas-overlay">
          <div className="part-canvas">
            <button className="close-button" onClick={handleCancel}>X</button>
            <h2>{isEditMode ? 'Edit Part' : 'Add Part'}</h2>
            <form onSubmit={handleFormSubmit} className="part-form">
              <div className="form-group">
                <label>Part:</label>
                <input 
                  type="text" 
                  name="part" 
                  value={formData.part} 
                  onChange={handleFormChange} 
                  required 
                />
              </div>
              <div className="form-group">
                <label>Description:</label>
                <textarea 
                  name="description" 
                  value={formData.description} 
                  onChange={handleFormChange} 
                  required 
                />
              </div>
              {/* Only display additional fields when editing */}
              {isEditMode && (
                <>
                  <div className="form-group">
                    <label>Dependencies (comma separated):</label>
                    <input 
                      type="text" 
                      name="dependencies" 
                      value={formData.dependencies} 
                      onChange={handleFormChange} 
                    />
                  </div>
                  <div className="form-group">
                    <label>Vehicles (comma separated):</label>
                    <input 
                      type="text" 
                      name="vehicles" 
                      value={formData.vehicles} 
                      onChange={handleFormChange} 
                    />
                  </div>
                  <div className="form-group">
                    <label>Price:</label>
                    <input 
                      type="number" 
                      name="price" 
                      value={formData.price} 
                      onChange={handleFormChange} 
                    />
                  </div>
                      <div className="form-group">
                        <label className="ready-toggle">
                          <input
                            type="checkbox"
                            name="ready"
                            checked={formData.ready}
                            onChange={(e) => setFormData((prev) => ({ ...prev, ready: e.target.checked }))}
                          />
                          Ready
                        </label>
                      </div>
                </>
              )}
              <div className="form-buttons">
                <button type="submit">{isEditMode ? 'Update Part' : 'Add Part'}</button>
                <button type="button" onClick={handleCancel}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SetPart;